"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createTicket,
  updateTicket,
  submitTicket as submitTicketService,
  createModifiedTicket as createModifiedTicketService,
  Ticket,
  CreateTicketParams,
  UpdateTicketParams,
  SubmitTicketParams,
  CreateModifiedTicketParams,
  TicketCanonicalFieldsLockedError,
} from "@/services/ticketService";
import { logger } from "@/utils/logger";

/**
 * Hook providing React Query mutations for all ticket operations
 * Replaces direct service calls with React Query mutations for better error handling and cache management
 */
export function useTicketMutations() {
  const queryClient = useQueryClient();

  /**
   * Mutation for creating a new ticket
   */
  const createMutation = useMutation<Ticket, Error, CreateTicketParams>({
    mutationFn: createTicket,
    onSuccess: ticket => {
      // Invalidate ticket queries to refetch if needed
      queryClient.invalidateQueries({ queryKey: ["ticket", ticket.id] });
      // Invalidate tickets list queries
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      logger.debug("Ticket created successfully", {
        component: "useTicketMutations",
        ticketId: ticket.id,
      });
    },
    onError: error => {
      logger.error(
        "Failed to create ticket",
        {
          component: "useTicketMutations",
        },
        error
      );
    },
  });

  /**
   * Mutation for updating an existing ticket
   */
  const updateMutation = useMutation<
    Ticket | undefined,
    Error,
    UpdateTicketParams
  >({
    mutationFn: updateTicket,
    onSuccess: (_, variables) => {
      // Invalidate the specific ticket query
      queryClient.invalidateQueries({
        queryKey: ["ticket", variables.ticketId],
      });
      // Invalidate tickets list queries
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      logger.debug("Ticket updated successfully", {
        component: "useTicketMutations",
        ticketId: variables.ticketId,
      });
    },
    onError: (error, variables) => {
      const context = {
        component: "useTicketMutations",
        ticketId: variables.ticketId,
      };
      if (error instanceof TicketCanonicalFieldsLockedError) {
        logger.warn("Rejected stale ticket design update", context);
        return;
      }
      logger.error("Failed to update ticket", context, error);
    },
  });

  /**
   * Mutation for submitting a ticket (creates or updates)
   */
  const submitMutation = useMutation<Ticket, Error, SubmitTicketParams>({
    mutationFn: submitTicketService,
    onSuccess: ticket => {
      // Invalidate the specific ticket query
      queryClient.invalidateQueries({ queryKey: ["ticket", ticket.id] });
      // Invalidate tickets list queries
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      logger.debug("Ticket submitted successfully", {
        component: "useTicketMutations",
        ticketId: ticket.id,
      });
    },
    onError: error => {
      logger.error(
        "Failed to submit ticket",
        {
          component: "useTicketMutations",
        },
        error
      );
    },
  });

  /**
   * Mutation for creating a modified ticket
   */
  const createModifiedMutation = useMutation<
    Ticket,
    Error,
    CreateModifiedTicketParams
  >({
    mutationFn: createModifiedTicketService,
    onSuccess: ticket => {
      // Invalidate ticket queries
      queryClient.invalidateQueries({ queryKey: ["ticket", ticket.id] });
      // Invalidate tickets list queries
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      logger.debug("Modified ticket created successfully", {
        component: "useTicketMutations",
        ticketId: ticket.id,
      });
    },
    onError: error => {
      logger.error(
        "Failed to create modified ticket",
        {
          component: "useTicketMutations",
        },
        error
      );
    },
  });

  return {
    createMutation,
    updateMutation,
    submitMutation,
    createModifiedMutation,
  };
}
